import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Button from '@components/Shared/Buttons/Button';
import TextField from '@components/Shared/TextField/TextField';
import DropDownList from '@components/Shared/DropDownList/DropDownList';

import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import {
  getQueryParams,
  buildQueryString,
} from 'pubweb-smokey/dist/utils/utils';

import { debounceEvent } from 'pubweb-smokey/dist/utils/utils';

import { padItems } from '@utils/formatters';
import { getResources, searchResources } from '@services/newsroomService';

import ScrollToTopButton from '@components/Shared/ScrollUpButton/ScrollUpButtonContainer';

import EmptySvg from '../../../../public/public-assets/images/newsroom/empty-results.svg';
import ClaytonLogo from '@components/Shared/ClaytonLogo/ClaytonLogo';
import Hero from '@components/Newsroom/Hero/Hero';
import ResourceCard from '@components/Newsroom/ResourceCard/ResourceCard';

import ResourcesStyles from './Resources.styled';

const CARDS_PER_VIEW = 20;

const Resources = ({ ssrResources }) => {
  const router = useRouter();
  const [initValues] = useState(getQueryParams());
  const [resourcesViewCount, setResourcesViewCount] = useState(CARDS_PER_VIEW);
  const [resources, setResources] = useState(ssrResources || []);
  const [resourcesView, setResourcesView] = useState(
    (ssrResources || []).slice(0, resourcesViewCount)
  );
  const [yearFilter, setYearFilter] = useState(initValues.year);
  const [resourcesYears, setResourcesYears] = useState([]);

  const textRef = useRef();

  const filterResources = (resourceList) => {
    if (isNaN(parseInt(yearFilter))) {
      setResourcesView(resourceList);
    } else {
      setResourcesView(
        resourceList.filter((resource) => {
          return new Date(resource.publicationDate).getFullYear() == yearFilter;
        })
      );
    }

    router.replace(
      window.location.pathname +
        buildQueryString({
          year: parseInt(yearFilter) || '',
          text: textRef.current.value,
        })
    );
  };

  const fireGTMEvent = () => {
    if (yearFilter || textRef.current.value) {
      pushGTMEvent('newsroom_event', {
        searchFilters: {
          year: yearFilter,
          searchText: textRef.current.value,
        },
        eventLabel: 'search_submit',
        eventAction: 'search',
        eventCategory: 'web_navigation',
      });
    }
  };

  const buildResourceYears = () => {
    let resourceYearTable = {};
    const availableYears = [];

    resources.forEach((resource) => {
      resourceYearTable[new Date(resource.publicationDate).getFullYear()] =
        true;
    });

    for (let year in resourceYearTable) {
      //eslint-disable-next-line no-prototype-builtins
      if (resourceYearTable.hasOwnProperty(year)) {
        availableYears.push(year);
      }
    }

    availableYears.sort((a, b) => b - a);
    availableYears.splice(0, 0, 'Any Year');

    setResourcesYears(availableYears);
  };

  const autoTextSearch = () => {
    fireGTMEvent();
    searchResources(textRef.current.value).then((result) => {
      setResources(result);
    });
  };

  if (!resources) {
    getResources()
      .then((result) => {
        setResources(result);
        filterResources(result);
        buildResourceYears();
      })
      .catch();
  }

  useEffect(() => {
    filterResources(resources);
    fireGTMEvent();
  }, [yearFilter]);

  useEffect(() => {
    filterResources(resources);
  }, [resources]);

  useEffect(() => {
    buildResourceYears();
    autoTextSearch();
  }, []);

  return (
    <div className="constrained-width-newsroom">
      <ResourcesStyles>
        <Head>
          <title>Resources | Clayton Newsroom</title>
          <meta
            name="description"
            content="View online resources to learn more about recent Clayton company news and announcements."
          />
          <meta property="og:image" content={ClaytonLogo} />
        </Head>
        <Hero>Resources</Hero>
        <ScrollToTopButton />
        <div className="resource-filters">
          <DropDownList
            className="resources-years"
            dropDownStyle="compact"
            id="year-filter"
            defaultValue={initValues.year}
            items={resourcesYears}
            onChange={(selectedYear) => {
              setYearFilter(selectedYear);
            }}
            label="Year"
          />

          <TextField
            textFieldStyle="compact"
            id="search"
            defaultValue={initValues.text}
            className="resources-search"
            label="Search"
            ref={textRef}
            onKeyUp={debounceEvent(autoTextSearch, 400)}
          />
        </div>
        <div className="resources-section">
          {resourcesView && resourcesView.length > 0 ? (
            <div className="resources-cards">
              {resourcesView
                .slice(0, resourcesViewCount)
                .map((resource, index) => {
                  return (
                    <ResourceCard
                      key={`resource-${index}`}
                      resource={resource}
                    />
                  );
                })}
              {padItems(resourcesView, 4)}
            </div>
          ) : (
            <div className="no-results">
              <EmptySvg />
              <p>No results found to match your search.</p>
            </div>
          )}
        </div>
        {resourcesView.length > resourcesViewCount ? (
          <div className="resources-actions">
            <Button
              className="gtm-load-more"
              onClick={(e) => {
                setResourcesViewCount(resourcesViewCount + CARDS_PER_VIEW);
                e.target.blur();
              }}
              buttonStyle="outlined"
            >
              LOAD MORE
            </Button>
          </div>
        ) : null}
      </ResourcesStyles>
    </div>
  );
};

Resources.propTypes = {
  ssrResources: PropTypes.array,
};

//eslint-disable-next-line no-unused-vars
export async function getServerSideProps() {
  let resources = null;

  await getResources()
    .then((result) => {
      resources = result;
    })
    .catch((error) => {
      console.error(`Error retrieving Newsroom Resources: ${error}`);
    });

  return {
    props: {
      ssrResources: resources,
    },
  };
}

export default Resources;
